import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Upload, X, FileImage } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { useGangSheetBuilderScript, getGangSheetSizes } from "@/hooks/useGangSheetBuilderScript";
import { getDataSource } from "@/lib/data-source";
import { GangSheetCanvas } from "@/components/GangSheetCanvas";
import {
  getCanvasConfig,
  saveLayout,
  type LocalItem,
  type CanvasConfig,
  heightToMetres,
} from "@/lib/gang-sheet";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { listActiveProducts, listActiveCuttingTiers, listShippingMethods } from "@/lib/products";
import { getAppSetting } from "@/lib/app-settings";
import { supabase } from "@/integrations/supabase/client";
import type { GangSheetSize } from "@/lib/types";
import { createNotification } from "@/lib/notifications";

export const Route = createFileRoute("/app/customer/new-job")({
  validateSearch: (search: Record<string, unknown>) => ({
    product: typeof search.product === "string" ? search.product : undefined,
  }),
  component: NewJob,
});

const STEPS = [
  {
    title: "Product",
    subtitle: "Choose your product type and specifications.",
    guidance: "Choose what you're printing and how long your sheet is.",
  },
  {
    title: "Artwork",
    subtitle: "Upload your artwork files or build a gang sheet.",
    guidance: "Build your gang sheet or upload artwork.",
  },
  {
    title: "Delivery",
    subtitle: "Select shipping method and delivery details.",
    guidance: "Confirm where we're sending your prints.",
  },
  {
    title: "Review",
    subtitle: "Review your job and estimated pricing.",
    guidance: "Check your design, size, and pricing before submitting.",
  },
] as const;

function NewJob() {
  const nav = useNavigate();
  const search = Route.useSearch();
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ["products"],
    queryFn: listActiveProducts,
  });

  const { data: cuttingTiers = [], isLoading: cuttingTiersLoading } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listActiveCuttingTiers,
  });

  const { data: shippingMethods = [], isLoading: shippingLoading } = useQuery({
    queryKey: ["shipping-methods"],
    queryFn: listShippingMethods,
  });

  const { data: pricingAnnouncement = "" } = useQuery({
    queryKey: ["pricing-announcement"],
    queryFn: () => getAppSetting("trade_pricing_announcement"),
  });

  const { data: gangsheetSizes = [] } = useQuery({
    queryKey: ["gangsheet_sizes"],
    queryFn: getGangSheetSizes,
  });

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null);

  const [jobTitle, setJobTitle] = useState("");
  const [productType, setProductType] = useState("");
  const [cuttingTierId, setCuttingTierId] = useState("");
  const [shippingMethodId, setShippingMethodId] = useState("");
  const [metres, setMetres] = useState(1);
  const [printSize, setPrintSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [colours, setColours] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState("");
  const [gangsheetDesignId, setGangsheetDesignId] = useState<string | null>(null);
  const [dripSizeId, setDripSizeId] = useState<number | null>(null);
  const [artworkMode, setArtworkMode] = useState<"canvas" | "external" | "manual">("canvas");

  // Canvas builder state
  const [canvasItems, setCanvasItems] = useState<LocalItem[]>([]);
  const [canvasConfig, setCanvasConfig] = useState<CanvasConfig | null>(null);
  const [canvasSaved, setCanvasSaved] = useState(false);
  const [canvasMetres, setCanvasMetres] = useState<number>(0);

  // Pre-select product from URL search param
  useEffect(() => {
    if (search?.product && products.length > 0) {
      const found = products.find((p) => p.name === search.product);
      if (found) setProductType(found.name);
    }
  }, [search?.product, products]);

  // Initialize defaults once data loads (run only when arrays first become non-empty)
  useEffect(() => {
    if (products.length > 0) setProductType((prev) => prev || products[0]?.name || "");
  }, [products]);

  useEffect(() => {
    if (cuttingTiers.length > 0) setCuttingTierId((prev) => prev || cuttingTiers[0]?.id || "");
  }, [cuttingTiers]);

  useEffect(() => {
    if (shippingMethods.length > 0)
      setShippingMethodId((prev) => prev || shippingMethods[0]?.id || "");
  }, [shippingMethods]);

  const {
    isLoaded: builderLoaded,
    isLoading: builderLoading,
    error: builderError,
    isEnabled: builderEnabled,
    shopId: dripShopId,
    openBuilder,
  } = useGangSheetBuilderScript();

  const selectedProduct = products.find((p) => p.name === productType);
  const useExternalBuilder = selectedProduct?.uses_external_builder && builderEnabled && dripShopId;
  const useCanvasBuilder = selectedProduct?.is_gangsheet ?? false;

  // Load canvas config when product changes
  useEffect(() => {
    if (selectedProduct?.id && useCanvasBuilder) {
      getCanvasConfig(selectedProduct.id).then(setCanvasConfig);
    }
  }, [selectedProduct?.id, useCanvasBuilder]);

  // Auto-select artworkMode when product changes
  useEffect(() => {
    if (useCanvasBuilder) setArtworkMode("canvas");
    else if (useExternalBuilder) setArtworkMode("external");
    else setArtworkMode("manual");
  }, [useCanvasBuilder, useExternalBuilder]);

  const handleCanvasSave = useCallback(
    async (
      items: LocalItem[],
      _heightMm: number,
      linearMetres: number,
      _util: number,
      _cost: number,
    ) => {
      setCanvasItems(items);
      setCanvasMetres(linearMetres);
      setMetres(linearMetres);
      setCanvasSaved(true);
      toast.success(
        `Gang sheet saved — ${linearMetres.toFixed(3)} m · ${items.length} item${items.length !== 1 ? "s" : ""}`,
      );
      // Auto-advance to next step so user doesn't have to press Next
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    },
    [],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => setFiles((prev) => [...prev, ...accepted]),
    accept: { "image/*": [], "application/pdf": [] },
  });

  const useBuilder = useExternalBuilder;

  const stepValid =
    step === 0
      ? jobTitle.trim().length > 0 && metres > 0
      : step === 1
        ? true
        : step === 2
          ? shippingMethodId
          : step === 3
            ? true
            : true;

  const openGangSheetBuilder = () => {
    if (!builderLoaded) {
      if (builderError) {
        toast.error(builderError);
      } else {
        toast.error("Builder is still loading, please try again");
      }
      return;
    }

    if (!dripShopId) {
      toast.error("Drip shop ID not configured. Please contact support.");
      return;
    }

    const gsBuilder = (window as any).gs_builder;
    if (!gsBuilder) {
      toast.error("Builder not available");
      return;
    }

    // IMPORTANT: gs_builder.open() resets eventListeners internally,
    // so .on() must be called AFTER .open().
    const builderRef = gsBuilder.open({
      shop_id: dripShopId,
      // Scope designs to this user so they can't see each other's work
      customer: {
        id: profile!.id,
        name: profile?.full_name ?? undefined,
        email: profile?.email ?? undefined,
      },
    });
    const b = builderRef ?? gsBuilder;

    const handleSaved = (e: any): void => {
      // Actual payload shape: { event, data: { id, ... } }
      const id = e?.data?.id ?? e?.data?.design_id ?? e?.design_id ?? e?.id ?? null;
      if (id) setGangsheetDesignId(String(id));
      toast.success("Gang sheet design saved — ready to continue");
      try {
        b.close?.();
      } catch {
        /* ignore */
      }
      setStep((s) => (s === 1 ? 2 : s));
    };
    b.on?.("design:created", handleSaved);
    b.on?.("design:updated", handleSaved);
  };

  const submit = async () => {
    if (!profile?.customer_id && !demoMode)
      return toast.error("No customer linked to your profile.");

    // Validate gangsheet_design_id if builder is required
    if (useCanvasBuilder && !canvasSaved) {
      toast.error("Please build and save your gang sheet before submitting");
      return;
    }
    if (useExternalBuilder && !gangsheetDesignId) {
      toast.error("Please build and save your gang sheet before submitting");
      return;
    }

    setSubmitting(true);
    try {
      const job = await ds.createJob({
        customer_id: profile?.customer_id ?? "c1",
        job_title: jobTitle,
        product_type: productType,
        cutting_tier_id: cuttingTierId || null,
        shipping_method_id: shippingMethodId || null,
        print_size: printSize,
        quantity,
        colours,
        due_date: dueDate || null,
        notes: notes || null,
        gangsheet_design_id: gangsheetDesignId || null,
        payment_status: "pending",
        metres: canvasMetres > 0 ? canvasMetres : metres,
      });

      // Persist canvas layout if built with canvas builder
      if (useCanvasBuilder && canvasItems.length > 0 && !demoMode) {
        try {
          await saveLayout(
            job.id,
            selectedProduct?.id ?? null,
            canvasItems,
            canvasConfig ?? {
              canvas_width_mm: 580,
              min_spacing_mm: 3,
              bleed_mm: 0,
              price_per_m: 0,
            },
            selectedProduct ? parseFloat(selectedProduct.price_per_m) : 0,
          );
        } catch (e) {
          console.error("Failed to save canvas layout", e);
        }
      }

      let uploadErrors = 0;
      for (const f of files) {
        try {
          await ds.addFile(job.id, f);
        } catch (e) {
          console.error(e);
          uploadErrors++;
        }
      }
      if (uploadErrors > 0) {
        toast.error(
          `Job submitted but ${uploadErrors} file${uploadErrors > 1 ? "s" : ""} failed to upload.`,
        );
      } else {
        toast.success("Job submitted!");
      }
      setSubmittedJobId(job.id);
      setStep(4); // Move to success screen

      // Notify staff that a new job was submitted
      try {
        const { data: staffRows } = await (supabase as any)
          .from("profiles")
          .select("id")
          .in("role", ["staff", "admin"]);
        const staffIds: string[] = (staffRows ?? [])
          .map((r: { id: string }) => r.id)
          .filter((rid: string) => rid && rid !== profile?.id);
        await Promise.all(
          staffIds.map((uid) =>
            createNotification({
              user_id: uid,
              type: "job_created",
              title: "New job submitted",
              message: `${profile?.full_name || profile?.email || "A customer"} submitted "${jobTitle}"`,
              data: { job_id: job.id },
              priority: 1,
            }).catch(() => null),
          ),
        );
      } catch (notifyErr) {
        console.error("Notify staff of new job failed:", notifyErr);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to submit job");
    } finally {
      setSubmitting(false);
    }
  };

  const current = STEPS[step];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-primary">
          Step {step + 1} of {STEPS.length}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Progress</span>
          <div className="h-2 w-32 overflow-hidden rounded-full bg-secondary">
            <motion.div
              className="h-full rounded-full bg-gradient-hero"
              initial={false}
              animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <span className="text-xs font-medium">
            {Math.round(((step + 1) / STEPS.length) * 100)}%
          </span>
        </div>
      </div>

      <div className="my-6 flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <button
            key={s.title}
            onClick={() => i < step && setStep(i)}
            disabled={i > step}
            className={cn(
              "flex flex-1 items-center gap-2 min-w-fit transition-opacity",
              i > step
                ? "opacity-50 cursor-not-allowed"
                : i < step
                  ? "cursor-pointer hover:opacity-80"
                  : "cursor-default",
            )}
          >
            <div
              className={cn(
                "grid h-12 w-12 shrink-0 place-items-center rounded-full text-sm font-semibold transition-all",
                i < step
                  ? "bg-gradient-hero text-primary-foreground"
                  : i === step
                    ? "bg-gradient-hero text-primary-foreground shadow-pop ring-4 ring-primary/15"
                    : "bg-secondary text-muted-foreground",
              )}
            >
              {i < step ? <Check className="h-5 w-5" /> : i + 1}
            </div>
            <div
              className={cn(
                "text-sm font-semibold whitespace-nowrap",
                i === step ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {s.title}
            </div>
            {i < STEPS.length - 1 && (
              <div className="hidden sm:block h-1 flex-1 overflow-hidden rounded-full bg-secondary min-w-8">
                <div
                  className={cn(
                    "h-full rounded-full bg-gradient-hero transition-all",
                    i < step ? "w-full" : "w-0",
                  )}
                />
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="mb-6 rounded-2xl bg-blue-50 border border-blue-200 p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How this works</h3>
        <p className="text-sm text-blue-800">
          You build your sheet in the gang sheet builder. When you save, we store the design and
          create a job in FlightDeck. We then print and bill you per metre as per the pricing
          section.
        </p>
      </div>

      <div className="rounded-[2.5rem] border border-border bg-card p-8 shadow-soft md:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mb-4 rounded-xl bg-secondary/50 p-3">
              <p className="text-sm text-muted-foreground">{current.guidance}</p>
            </div>
            {step === 0 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Job title *</Label>
                  <Input
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g. Tour merch — front design"
                    className="rounded-2xl"
                  />
                  {jobTitle.trim().length === 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Please add a job title so we know what to call this job.
                    </p>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Product type</Label>
                    <select
                      value={productType}
                      onChange={(e) => setProductType(e.target.value)}
                      className="w-full rounded-2xl border border-border bg-background px-4 py-2"
                    >
                      {products.map((product) => (
                        <option key={product.id} value={product.name}>
                          {product.name} (from £{product.price_per_m}/m)
                        </option>
                      ))}
                    </select>
                    {products.find((p) => p.name === productType)?.description && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {products.find((p) => p.name === productType)?.description}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Cutting preference per metre *</Label>
                    <select
                      value={cuttingTierId}
                      onChange={(e) => setCuttingTierId(e.target.value)}
                      className="w-full rounded-2xl border border-border bg-background px-4 py-2"
                    >
                      {cuttingTiers.map((tier) => (
                        <option key={tier.id} value={tier.id}>
                          {tier.name} – £{tier.price_per_m.toFixed(2)}/m
                        </option>
                      ))}
                    </select>
                    {cuttingTiers.find((t) => t.id === cuttingTierId)?.description && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {cuttingTiers.find((t) => t.id === cuttingTierId)?.description}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      We charge per full metre only. Cutting is priced per metre based on how many
                      items you need per spread.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Print size</Label>
                    <Input
                      value={printSize}
                      onChange={(e) => setPrintSize(e.target.value)}
                      placeholder="e.g. A3, 30x40cm"
                      className="rounded-2xl"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Metres (length) *</Label>
                    <Input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={metres}
                      onChange={(e) => setMetres(parseFloat(e.target.value) || 0.1)}
                      className="rounded-2xl"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the total length in metres for pricing calculation
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min={1}
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value || "1"))}
                      className="rounded-2xl"
                      placeholder="Number of items"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Colours</Label>
                    <Input
                      value={colours}
                      onChange={(e) => setColours(e.target.value)}
                      className="rounded-2xl"
                      placeholder="e.g. CMYK, White ink"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Due date</Label>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="rounded-2xl"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                {/* Canvas builder — primary for gang-sheet products */}
                {useCanvasBuilder && (
                  <div className="space-y-4">
                    {canvasSaved ? (
                      <div className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800/40 dark:bg-emerald-950/30">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-emerald-600" />
                          <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                            Gang sheet saved · {canvasItems.length} items ·{" "}
                            {canvasMetres.toFixed(3)} m
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => setCanvasSaved(false)}
                        >
                          Edit layout
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="rounded-2xl border border-border bg-card p-4"
                        style={{ minHeight: 520 }}
                      >
                        <GangSheetCanvas
                          config={canvasConfig ?? undefined}
                          pricePerM={selectedProduct ? parseFloat(selectedProduct.price_per_m) : 0}
                          onSave={handleCanvasSave}
                          initialItems={canvasItems}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* External builder (Drip) — for products configured with external builder */}
                {!useCanvasBuilder && useExternalBuilder && (
                  <div
                    className={cn(
                      "rounded-2xl border-2 p-6 transition-all",
                      "border-primary bg-primary/5",
                    )}
                  >
                    <h4 className="font-semibold text-lg mb-1">Use the gang sheet builder</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Build your gang sheet layout interactively with our builder tool.
                    </p>
                    {gangsheetDesignId ? (
                      <div className="flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium text-primary">
                          Gang sheet design saved
                        </span>
                      </div>
                    ) : (
                      <Button
                        onClick={openGangSheetBuilder}
                        disabled={builderLoading}
                        className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                      >
                        {builderLoading ? "Loading builder..." : "Build a Gang Sheet"}
                      </Button>
                    )}
                  </div>
                )}

                {/* Manual upload — for non-gang-sheet products or fallback */}
                {!useCanvasBuilder && (
                  <div
                    className={cn(
                      "rounded-2xl border-2 p-6 transition-all",
                      "border-border bg-secondary/40",
                    )}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-lg">
                          {useExternalBuilder ? "Upload a ready-made gang sheet" : "Upload artwork"}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {useExternalBuilder ? "(advanced option)" : "PNG, JPG or PDF"}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      If you already have a complete gang sheet at the correct size, you can upload
                      it here instead of using the builder. We won't cut these sheets; they will be
                      printed as supplied.
                    </p>
                    {
                      <div
                        {...getRootProps()}
                        className={cn(
                          "group relative rounded-[2rem] border-2 border-dashed p-12 text-center transition-all",
                          isDragActive
                            ? "border-primary bg-primary/5 scale-[1.02]"
                            : "border-border bg-secondary/40 hover:border-primary/50 hover:bg-secondary/60",
                        )}
                      >
                        <input {...getInputProps()} />
                        <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-gradient-hero text-primary-foreground shadow-soft group-hover:scale-110 transition-transform">
                          <Upload className="h-7 w-7" />
                        </div>
                        <p className="text-lg font-semibold text-foreground">
                          Drop files here or click to browse
                        </p>
                        <p className="mt-2 text-sm text-muted-foreground">
                          Supported formats: PNG, JPG, PDF
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          You can upload multiple files
                        </p>
                      </div>
                    }
                    {files.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <p className="text-sm font-medium text-foreground">
                          {files.length} file{files.length !== 1 ? "s" : ""} selected
                        </p>
                        <ul className="space-y-2">
                          {files.map((f, i) => (
                            <li
                              key={i}
                              className="flex items-center justify-between rounded-2xl border border-border bg-secondary/30 px-4 py-3 transition-colors hover:bg-secondary/50"
                            >
                              <div className="flex items-center gap-3">
                                <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10">
                                  <FileImage className="h-5 w-5 text-primary" />
                                </div>
                                <span className="text-sm font-medium">{f.name}</span>
                              </div>
                              <button
                                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                                className="rounded-full p-2 hover:bg-background transition-colors"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Shipping method *</Label>
                  <select
                    value={shippingMethodId}
                    onChange={(e) => setShippingMethodId(e.target.value)}
                    className="w-full rounded-2xl border border-border bg-background px-4 py-2"
                    required
                  >
                    {shippingMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.name} – £{method.price.toFixed(2)}
                      </option>
                    ))}
                  </select>
                  {shippingMethods.find((m) => m.id === shippingMethodId)?.description && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {shippingMethods.find((m) => m.id === shippingMethodId)?.description}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Notes & special instructions</Label>
                  <Textarea
                    rows={6}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="rounded-2xl"
                    placeholder="Anything we should know about colours, finish, deadlines…"
                  />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                {pricingAnnouncement && (
                  <div className="rounded-2xl bg-gradient-hero p-4 text-primary-foreground">
                    <p className="text-sm font-medium">{pricingAnnouncement}</p>
                  </div>
                )}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Job details
                  </h4>
                  <div className="rounded-2xl bg-secondary/40 p-5 space-y-3">
                    <Field k="Job title" v={jobTitle} />
                    <Field k="Product" v={productType} />
                    <Field
                      k="Cutting"
                      v={cuttingTiers.find((t) => t.id === cuttingTierId)?.name || "—"}
                    />
                    <Field k="Metres" v={`${metres} m`} />
                    <Field k="Size" v={printSize} />
                    <Field k="Quantity" v={String(quantity)} />
                    <Field k="Colours" v={colours} />
                    <Field
                      k="Shipping"
                      v={shippingMethods.find((m) => m.id === shippingMethodId)?.name || "—"}
                    />
                    <Field k="Due date" v={dueDate || "—"} />
                    <Field k="Notes" v={notes || "—"} />
                    {gangsheetDesignId && <Field k="Gang sheet design ID" v={gangsheetDesignId} />}
                  </div>
                </div>
                {/* Builder size mapping */}
                {dripSizeId &&
                  (() => {
                    const size = gangsheetSizes.find((s) => s.drip_size_id === dripSizeId);
                    if (size) {
                      return (
                        <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4">
                          <p className="text-sm text-blue-900">
                            <strong>Builder size:</strong> {size.width}x{size.height} {size.unit}{" "}
                            (mapped to {size.metres_equivalent} metre
                            {size.metres_equivalent !== 1 ? "s" : ""} @ £
                            {parseFloat(
                              products.find((p) => p.name === productType)?.price_per_m ?? "0",
                            ).toFixed(2)}
                            /m)
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                {/* Quote Summary */}
                {(() => {
                  const product = products.find((p) => p.name === productType);
                  const cuttingTier = cuttingTiers.find((t) => t.id === cuttingTierId);
                  const shippingMethod = shippingMethods.find((m) => m.id === shippingMethodId);

                  const productCost = product ? parseFloat(product.price_per_m) * metres : 0;
                  const cuttingCost = cuttingTier ? cuttingTier.price_per_m * metres : 0;
                  const shippingCost = shippingMethod ? shippingMethod.price : 0;
                  const total = productCost + cuttingCost + shippingCost;

                  return (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        Quote summary
                      </h4>
                      <div className="rounded-2xl bg-gradient-soft p-5 space-y-3">
                        {product ? (
                          <div className="flex justify-between items-center text-sm">
                            <span>
                              Product ({metres}m @ £{parseFloat(product.price_per_m).toFixed(2)}/m)
                            </span>
                            <span className="font-medium">£{productCost.toFixed(2)}</span>
                          </div>
                        ) : null}
                        {cuttingTier ? (
                          <div className="flex justify-between items-center text-sm">
                            <span>
                              Cutting ({metres}m @ £{cuttingTier.price_per_m.toFixed(2)}/m)
                            </span>
                            <span className="font-medium">£{cuttingCost.toFixed(2)}</span>
                          </div>
                        ) : null}
                        {shippingMethod ? (
                          <div className="flex justify-between items-center text-sm">
                            <span>Shipping</span>
                            <span className="font-medium">£{shippingCost.toFixed(2)}</span>
                          </div>
                        ) : null}
                        <div className="border-t border-border pt-3 flex justify-between items-center font-semibold">
                          <span>Estimated total (excl. VAT)</span>
                          <span>£{total.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Prices are per full metre. Final invoicing is handled by Resolute Trade
                          Print.
                        </p>
                      </div>
                    </div>
                  );
                })()}
                {/* Payment confirmation */}
                <div className="rounded-2xl bg-card border border-border p-5 space-y-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Payment & confirmation
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Trade print work is billed via your Resolute account. We'll confirm pricing and
                    send an invoice as usual.
                  </p>
                </div>
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Artwork files
                  </h4>
                  <div className="rounded-2xl bg-secondary/40 p-5">
                    {files.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No files uploaded</p>
                    ) : (
                      <ul className="space-y-2">
                        {files.map((f, i) => (
                          <li key={i} className="flex items-center gap-3 text-sm">
                            <div className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 shrink-0">
                              <FileImage className="h-4 w-4 text-primary" />
                            </div>
                            <span className="truncate">{f.name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            {step === 4 && submittedJobId && (
              <div className="text-center py-12">
                <div className="mx-auto mb-6 grid h-20 w-20 place-items-center rounded-full bg-gradient-hero text-primary-foreground shadow-soft">
                  <Check className="h-10 w-10" />
                </div>
                <h2 className="text-2xl font-bold">Job submitted!</h2>
                <p className="mt-3 text-muted-foreground">
                  You'll see it on your dashboard in a moment.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link to="/app/customer/jobs/$id" params={{ id: submittedJobId }}>
                    <Button className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90">
                      View this job
                    </Button>
                  </Link>
                  <Link to="/app/customer/dashboard">
                    <Button variant="outline" className="rounded-full">
                      Back to dashboard
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {step < 4 && (
          <div className="mt-10 flex justify-between">
            <Button
              variant="ghost"
              disabled={step === 0}
              onClick={() => setStep(step - 1)}
              className="rounded-full px-6"
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                disabled={!stepValid}
                onClick={() => setStep(step + 1)}
                className="rounded-full bg-gradient-hero px-8 text-primary-foreground shadow-soft hover:opacity-90"
              >
                Next <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                disabled={submitting}
                onClick={submit}
                className="rounded-full bg-gradient-hero px-8 text-base font-semibold text-primary-foreground shadow-soft hover:opacity-90"
              >
                {submitting ? "Submitting…" : "Submit job"}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
      <dd className="font-medium">{v}</dd>
    </div>
  );
}
